/**
 * `windows` — the **GMS//OS window registry** for COMBAT.app's floating windows (the EDIT
 * inspectors, the DEPLOY picker, the Map key). It is a **pure model** (the `WindowRegistry`
 * transitions: open / close / focus / move / resize, unit-testable with no React or DOM) plus the
 * thin {@link useCombatWindows} React hook that holds the registry in `useState` and exposes
 * setters. Open ids live back→front in `order`, and `zOf` is `40 + index`, so windows stay in
 * `[40, 60)`. Geometry persists across a close/reopen within the session (a reopened window
 * returns to where the GM left it); it is never URL-bearing. **No auto-open** — a window enters
 * `order` only through an explicit `open`/`toggle` call.
 *
 * **%-truth, px derived.** Geometry is stored as **workspace percentages** ({@link PctGeometry}),
 * and the pixel rect {@link WindowFrame} consumes is derived per render against the *live*
 * workspace ({@link pctToPx}), so every open combat window (stable-id AND dynamic inspectors
 * alike) reflows on a resize instead of drifting off-screen. The pure transitions below
 * (`openWindow`/`moveWindow`/`resizeWindow`) take the live `Viewport` and convert at the boundary
 * via {@link posToPct}/{@link sizeToPct}/{@link expandSpec} — pixels reported up by a frame
 * gesture never become the stored truth. Every conversion reuses the pure codec in
 * `../../routes/home/windowGeometry.ts`.
 *
 * **Per-mode localStorage persistence, STABLE-id windows only.** Each mode hydrates its geometry from its own
 * `lvtt:windows:combat:<mode>` blob on mount ({@link hydrateModeRegistries}) and writes back
 * **debounced ~200ms** after the last change. Only STABLE-id windows (`units`/`terrain`/`deploy-picker`/
 * `prompt`/`dice` — anything that ISN'T a dynamic `inspector:<id>`/`room-inspector:<id>`, per
 * {@link isStableWindowId}) are ever written to or read from the blob — the dynamic per-unit/
 * per-room inspectors keep reflowing but stay session-ephemeral: their geometry
 * is never persisted, and a stored dynamic key (there shouldn't be one, but a hand-edited/stale
 * blob might carry one) is dropped on hydrate rather than crashing or applying.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { storageGetJson, storageSetJson } from "../browserStorage";
import { inspectorCombatantId, roomInspectorRoomId } from "./combatInspector";
import {
  DEFAULT_MIN,
  expandSpec,
  FALLBACK_VIEWPORT,
  isPctGeometry,
  PAD_LEFT,
  PAD_TOP,
  pctToPx,
  posToPct,
  pxToPct,
  resolveDockZone,
  resolveResizeSnap,
  resolveSnap,
  sizeToPct,
  zoneGeometry,
  type DockZone,
  type PctGeometry,
  type Rect,
  type ResizeHandle,
  type Viewport,
  type WindowPos,
  type WindowSize,
} from "../../routes/home/windowGeometry";

/** A window's live geometry (top-left + card size) in pixels, driven by the {@link WindowFrame} gestures. */
export type WindowGeometry = { pos: WindowPos; size: WindowSize };

/**
 * The snap-aware lifecycle bundle {@link useCombatWindows}' `windowProps(id)` returns for one
 * window — spread straight onto a {@link WindowFrame} in place of hand-wired `onPos`/`onSize`.
 * Deliberately narrow:
 * it omits `pos`/`z`/`onFocus` — the combat hosts (`CombatWindow`, the bare PROMPT block) already
 * derive those themselves via `geometryOf`/`zOf`/`focus` for their own open/null guards and Esc-layer
 * wiring, so this bundle only owns the drag/resize gesture surface.
 */
export type CombatWindowProps = {
  onPos: (p: WindowPos) => void;
  size: WindowSize;
  onSize: (s: WindowSize) => void;
  windowId: string;
  onDragStart: () => void;
  onDragEnd: () => void;
  onResizeStart: (handle: ResizeHandle) => void;
  onResize: (geo: { pos: WindowPos; size: WindowSize }) => void;
  onResizeEnd: () => void;
};

/**
 * A window's static spec: its id and where/how large it first opens (a pixel rect — expanded to % on
 * first open). In COMBAT.app `defaultPos.x` is an offset into the free lane, which
 * `firstOpenGeometry` (`windowLane.ts`) turns into an absolute x; `defaultPos.y` is absolute.
 */
export type CombatWindowSpec = {
  id: string;
  defaultPos: WindowPos;
  defaultSize: WindowSize;
};

/** Read the live viewport (CSS px), falling back to a fixed size off-DOM (tests / SSR). */
export const readViewport = (): Viewport =>
  typeof window === "undefined" ? FALLBACK_VIEWPORT : { w: window.innerWidth, h: window.innerHeight };

/**
 * The pure registry state: the open window ids in z-order (back-most first,
 * front-most last) plus per-window geometry stored as **viewport percentages**
 * (the stored source of truth — `PctGeometry`, `pos`/`size` in px are derived per
 * render). A window is open iff its id is in `order`; geometry outlives a close so
 * a reopen restores the GM's last placement.
 */
export type WindowRegistry = {
  order: string[];
  geometry: Record<string, PctGeometry>;
};

/** The empty registry — nothing open (the initial combat surface, no auto-open). */
export const emptyRegistry = (): WindowRegistry => ({ order: [], geometry: {} });

/** Whether a window is currently open. */
export function isOpen(reg: WindowRegistry, id: string): boolean {
  return reg.order.includes(id);
}

/**
 * The z-index of a window: front-most highest, all kept in `[40, 60)`. A closed/absent window reports the base `40`.
 */
export function zOf(reg: WindowRegistry, id: string): number {
  const idx = reg.order.indexOf(id);
  return idx < 0 ? 40 : 40 + idx;
}

/** Lift `id` to the front of an order (no-op if already frontmost or absent). */
function liftToFront(order: string[], id: string): string[] {
  if (!order.includes(id) || order[order.length - 1] === id) return order;
  return [...order.filter((x) => x !== id), id];
}

/**
 * Open a window and focus it to the front. Idempotent on the open set — reopening
 * an already-open window just raises it. Geometry is seeded from the spec's pixel
 * `defaultPos`/`defaultSize` only on the *first* open, expanded to a % rect against
 * `vp` via {@link expandSpec} (no new math — the same px→% boundary conversion the
 * gesture setters use); a reopen keeps the prior % geometry (the GM's last placement).
 */
export function openWindow(reg: WindowRegistry, spec: CombatWindowSpec, vp: Viewport): WindowRegistry {
  const order = liftToFront(reg.order.includes(spec.id) ? reg.order : [...reg.order, spec.id], spec.id);
  const geometry = reg.geometry[spec.id]
    ? reg.geometry
    : {
        ...reg.geometry,
        [spec.id]: expandSpec(
          { kind: "px", x: spec.defaultPos.x, y: spec.defaultPos.y, w: spec.defaultSize.w, h: spec.defaultSize.h },
          vp,
        ),
      };
  return { order, geometry };
}

/** Close a window (drops it from `order`; geometry is retained for a later reopen). */
export function closeWindow(reg: WindowRegistry, id: string): WindowRegistry {
  if (!reg.order.includes(id)) return reg;
  return { ...reg, order: reg.order.filter((x) => x !== id) };
}

/** Raise an open window to the front (no-op if closed or already frontmost). */
export function focusWindow(reg: WindowRegistry, id: string): WindowRegistry {
  const order = liftToFront(reg.order, id);
  return order === reg.order ? reg : { ...reg, order };
}

/** Args for {@link moveWindow} (four fields — max-params-3 discipline, `CODE_STYLE.md`). */
export type MoveWindowArgs = { reg: WindowRegistry; id: string; pos: WindowPos; vp: Viewport };

/**
 * Record a window's new top-left (no-op if the window has no geometry yet). `pos` is
 * the pixel position a frame drag gesture reports; it's converted to % against `vp`
 * at this boundary via {@link posToPct} — pixels never become the stored truth.
 */
export function moveWindow(args: MoveWindowArgs): WindowRegistry {
  const { reg, id, pos, vp } = args;
  const g = reg.geometry[id];
  if (!g) return reg;
  return { ...reg, geometry: { ...reg.geometry, [id]: { ...g, ...posToPct(pos, vp) } } };
}

/** Args for {@link resizeWindow} (four fields — max-params-3 discipline, `CODE_STYLE.md`). */
export type ResizeWindowArgs = { reg: WindowRegistry; id: string; size: WindowSize; vp: Viewport };

/**
 * Record a window's new card size (no-op if the window has no geometry yet). `size` is
 * the pixel size a frame resize gesture reports; it's converted to % against `vp`
 * at this boundary via {@link sizeToPct} — pixels never become the stored truth.
 */
export function resizeWindow(args: ResizeWindowArgs): WindowRegistry {
  const { reg, id, size, vp } = args;
  const g = reg.geometry[id];
  if (!g) return reg;
  return { ...reg, geometry: { ...reg.geometry, [id]: { ...g, ...sizeToPct(size, vp) } } };
}

/** Args for {@link setWindowGeometry} (four fields — max-params-3 discipline, `CODE_STYLE.md`). */
export type SetGeometryArgs = { reg: WindowRegistry; id: string; geo: { pos: WindowPos; size: WindowSize }; vp: Viewport };

/**
 * Record a window's new pos AND size together (no-op if the window has no geometry yet).
 * `moveWindow`/`resizeWindow` only ever touch one field each; a west/north-edge resize
 * ({@link resizeGeometry} in `windowGeometry.ts`) moves the origin *and* the size in the SAME
 * gesture, so the snap-aware resize path (`useCombatWindows`' `windowProps(id).onResize`) needs
 * a setter that lands both in one store update. `geo` is the pixel rect a frame resize
 * gesture reports (already snapped); converted to % against `vp` via {@link pxToPct} at this
 * boundary.
 */
export function setWindowGeometry(args: SetGeometryArgs): WindowRegistry {
  const { reg, id, geo, vp } = args;
  const g = reg.geometry[id];
  if (!g) return reg;
  return { ...reg, geometry: { ...reg.geometry, [id]: pxToPct(geo, vp) } };
}

/** The two combat "virtual desktops" — each its own open-set + geometry. */
export type CombatMode = "edit" | "play";

/**
 * The **per-mode registries**: EDIT and PLAY each own a full {@link WindowRegistry}.
 * A mode-flip *parks* the current mode's windows (they stop
 * rendering but their registry — open-set + geometry — is untouched) and *restores*
 * the other mode's, simply by reading a different slot. There is no explicit
 * park/restore step and no "global" carve-out: parking falls out of keying the
 * registry by mode. A window that is a singleton on *both* desktops (e.g. the Log)
 * has independent open-state per mode — that's the per-mode model, not a bug.
 */
export type ModeRegistries = Record<CombatMode, WindowRegistry>;

/** The empty per-mode registries — nothing open on either desktop (no auto-open). */
export const emptyModeRegistries = (): ModeRegistries => ({
  edit: emptyRegistry(),
  play: emptyRegistry(),
});

// ── Per-mode localStorage persistence, STABLE-id windows only ───────────────────────────

/**
 * Whether `id` names a STABLE window (a module-constant spec — `units`/`terrain`/
 * `deploy-picker`/`prompt`/`dice`) rather than a DYNAMIC per-unit/per-room inspector minted at
 * runtime (`inspector:<combatantId>` / `room-inspector:<roomId>`). Only stable windows are
 * ever written to or read from the persisted blob — reuses the SAME
 * prefix predicates {@link inspectorCombatantId}/{@link roomInspectorRoomId} the dynamic
 * specs are minted from, rather than an allowlist that would need updating for a new stable
 * window.
 */
function isStableWindowId(id: string): boolean {
  return inspectorCombatantId(id) === null && roomInspectorRoomId(id) === null;
}

/** The localStorage key for a mode's persisted stable-window geometry. */
export const combatStorageKey = (mode: CombatMode): string => `lvtt:windows:combat:${mode}`;

/**
 * Hydrate one mode's persisted STABLE-window geometry from `localStorage` — the mount-time
 * seed for {@link hydrateModeRegistries}. A malformed/unparseable/absent blob degrades to
 * `{}` (every window falls back to its spec default the first time it's opened, via
 * `openWindow`'s existing seed-on-first-open — no migrations). Per key: a
 * DYNAMIC id is dropped (dynamic inspectors never persist, even if a stale/hand-edited
 * blob carries one) and a value that isn't a valid {@link PctGeometry} is dropped too.
 */
function hydrateGeometry(mode: CombatMode): Record<string, PctGeometry> {
  // An unreachable store, an unset key, an unparseable blob and a non-object blob all
  // degrade to `{}` — every window then falls back to its spec default on first open.
  const read = storageGetJson<unknown>(combatStorageKey(mode));
  const parsed = read.ok ? read.value : undefined;
  const stored: Record<string, unknown> = parsed !== null && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  const out: Record<string, PctGeometry> = {};
  for (const [id, value] of Object.entries(stored)) {
    if (isStableWindowId(id) && isPctGeometry(value)) out[id] = value;
  }
  return out;
}

/**
 * The initial per-mode registries, seeded from each mode's persisted STABLE-window geometry.
 * `order` stays empty on both — hydrate never auto-opens a window (the workspace's
 * no-default-opening rule); a hydrated window's geometry only takes effect once the GM
 * explicitly opens it, since `openWindow` already keeps an existing `reg.geometry[id]` over
 * the spec default. The mount-time seed for {@link useCombatWindows}.
 */
export const hydrateModeRegistries = (): ModeRegistries => ({
  edit: { order: [], geometry: hydrateGeometry("edit") },
  play: { order: [], geometry: hydrateGeometry("play") },
});

/** A mode's geometry, filtered down to STABLE-id windows only — the thin transform
 * between the live registry and what gets written to `localStorage`. Dynamic `inspector:`/
 * `room-inspector:` keys are dropped even though they're reflowing live; they just
 * never touch storage. Exported for the persistence unit tests (the serialize half of the
 * round-trip + the stable-only filter proof) — {@link persistGeometry} is the effectful
 * storage-writing wrapper around it that `useCombatWindows`' write-back effect calls. */
export function stableGeometry(reg: WindowRegistry): Record<string, PctGeometry> {
  const out: Record<string, PctGeometry> = {};
  for (const [id, g] of Object.entries(reg.geometry)) {
    if (isStableWindowId(id)) out[id] = g;
  }
  return out;
}

/** Write one mode's STABLE-window geometry to `localStorage`. Swallows storage
 * errors (full/unavailable — private-mode browsing, quota) so a write failure never crashes
 * the surface; geometry just stays in-memory-only for that mode. */
function persistGeometry(mode: CombatMode, reg: WindowRegistry): void {
  // A failed write is swallowed (storage full / unavailable) — geometry stays in-memory.
  storageSetJson(combatStorageKey(mode), stableGeometry(reg));
}

/**
 * Apply a registry transition to a single mode's slot, leaving the other mode's
 * registry referentially untouched (so a flip never disturbs the parked desktop).
 * Returns the same object when the transition was a no-op.
 */
export function updateMode(
  regs: ModeRegistries,
  mode: CombatMode,
  fn: (r: WindowRegistry) => WindowRegistry,
): ModeRegistries {
  const next = fn(regs[mode]);
  return next === regs[mode] ? regs : { ...regs, [mode]: next };
}

/**
 * The combat-route window registry hook, **per-mode**: holds one
 * {@link WindowRegistry} per desktop (EDIT / PLAY) in React state and routes every
 * open/close/focus/move/resize setter — and the `isOpen`/`zOf`/`geometryOf` reads —
 * to the *active* `mode`'s registry. Flipping `mode` parks the current desktop's
 * windows and restores the other's with no close: the inactive registry is simply
 * not read this render (its geometry persists for the return flip).  *
 * **Live-viewport reflow.** A `window` resize
 * listener
 * keeps a live `Viewport` in state; `geometryOf` derives each open window's pixel rect
 * from its stored % geometry against that live viewport every render ({@link pctToPx}),
 * so every open window — stable-id AND dynamic inspectors alike — reflows fluidly on
 * resize. The `move`/`resize` setters convert the gesture's reported px back to % before
 * storing (the boundary conversion lives in `moveWindow`/`resizeWindow` themselves).
 *
 * **Per-mode localStorage persistence.** The
 * initial state hydrates each mode's geometry from its own `lvtt:windows:combat:<mode>` blob
 * ({@link hydrateModeRegistries}) — `order` stays empty, so nothing auto-opens; a hydrated
 * window's geometry only surfaces once the GM opens it. A debounced (~200ms) effect writes
 * each mode's STABLE-window geometry back after the last change.
 *
 * **Snap-during-drag + snap-during-resize.** The
 * neighbor-snapshot + snap-aware move/resize machinery lives in this hook, since the snap has to
 * fire *inside* its own `move`/`resize` path. The
 * gesture state (`dragIdRef`/`neighborsRef`/`resizeHandleRef`/`resizeNeighborsRef`) is ONE set,
 * not per-mode: only the active mode's windows are mounted in the DOM, so `snapshotNeighbors`
 * naturally reads just the active mode's cards, and the state clears on every drag/resize end
 * regardless of mode. `windowProps(id)` is the bundle a host spreads onto a {@link WindowFrame} —
 * its `onPos` is guarded on `dragIdRef.current === id` so a programmatic reflow never snaps, only a live drag does.
 *
 * **First-open placement.** `placeFirstOpen`, when given, places a spec before `open`/`toggle`
 * seeds it: COMBAT.app passes the lane placement (`windowLane.ts`), which measures the layout at
 * open time. It changes nothing for a window that already has geometry.
 *
 * **Drag-to-edge dock zones.** `beginDrag` additionally starts
 * a live cursor-tracking `mousemove` listener; each move resolves the cursor's dock zone
 * ({@link resolveDockZone}) and mirrors it into `zoneRef`/`zone` state so the preview overlay
 * ({@link zonePreview}) re-renders. `endDrag` commits a hit zone's geometry
 * ({@link zoneGeometry}) — overriding the dragged position — BEFORE clearing the gesture refs and
 * tearing the listener down; a belt-and-braces unmount effect also tears it down if the surface
 * unmounts mid-drag. Dock zones are drag-only, `beginResize`/`endResize` are untouched.
 */
export function useCombatWindows(
  mode: CombatMode,
  placeFirstOpen?: (spec: CombatWindowSpec) => CombatWindowSpec,
  workspace?: Element | null,
) {
  const [registries, setRegistries] = useState<ModeRegistries>(hydrateModeRegistries);

  // Debounced write-back (~200ms after the last change). Only
  // STABLE-id windows are ever written (`persistGeometry` filters); dynamic inspectors keep
  // reflowing live but never touch storage.
  useEffect(() => {
    const t = setTimeout(() => {
      persistGeometry("edit", registries.edit);
      persistGeometry("play", registries.play);
    }, 200);
    return () => clearTimeout(t);
  }, [registries]);

  // The workspace windows live in: COMBAT's own box on the shell's stage when given, else the
  // browser viewport. Geometry is in the workspace's coordinates; a pointer or a DOM rect read in
  // client coordinates is shifted by `originOf()` first.
  const measureVp = useCallback((): Viewport => {
    if (!workspace) return readViewport();
    const r = workspace.getBoundingClientRect();
    return { w: r.width, h: r.height };
  }, [workspace]);
  const originOf = useCallback((): { left: number; top: number } => {
    if (!workspace) return { left: 0, top: 0 };
    const r = workspace.getBoundingClientRect();
    return { left: r.left, top: r.top };
  }, [workspace]);
  const [vp, setVp] = useState<Viewport>(readViewport);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setVp(measureVp());
    onResize();
    window.addEventListener("resize", onResize);
    const ro = workspace && typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : undefined;
    if (ro && workspace) ro.observe(workspace);
    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [workspace, measureVp]);

  const apply = useCallback(
    (fn: (r: WindowRegistry) => WindowRegistry) => setRegistries((rs) => updateMode(rs, mode, fn)),
    [mode],
  );
  const reg = registries[mode];
  // `placeFirstOpen` measures the live layout, so it runs here at open time, outside the state
  // updater. `openWindow` reads the placed spec only when the window has no geometry yet.
  const place = useCallback((spec: CombatWindowSpec) => (placeFirstOpen ? placeFirstOpen(spec) : spec), [placeFirstOpen]);
  const open = useCallback(
    (spec: CombatWindowSpec) => {
      const placed = place(spec);
      apply((r) => openWindow(r, placed, vp));
    },
    [apply, place, vp],
  );
  const close = useCallback((id: string) => apply((r) => closeWindow(r, id)), [apply]);
  const focus = useCallback((id: string) => apply((r) => focusWindow(r, id)), [apply]);
  const move = useCallback((id: string, pos: WindowPos) => apply((r) => moveWindow({ reg: r, id, pos, vp })), [apply, vp]);
  const resize = useCallback((id: string, size: WindowSize) => apply((r) => resizeWindow({ reg: r, id, size, vp })), [apply, vp]);
  const toggle = useCallback(
    (spec: CombatWindowSpec) => {
      const placed = place(spec);
      apply((r) => (isOpen(r, spec.id) ? closeWindow(r, spec.id) : openWindow(r, placed, vp)));
    },
    [apply, place, vp],
  );

  // ---- snap-during-drag + snap-during-resize -------------------------------------------
  // ONE gesture-state set, not per-mode: which window is being title-bar dragged / resized,
  // the neighbour CARD rects snapshotted from the DOM at gesture start (excludes the gesturing
  // window itself), and the gripped resize handle. Live in refs so `windowProps`' per-frame
  // `onPos`/`onResize` read them without forcing new callback identities each render.
  const dragIdRef = useRef<string | null>(null);
  const neighborsRef = useRef<Rect[]>([]);
  const zoneRef = useRef<DockZone | null>(null);
  const cursorMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const [zone, setZone] = useState<DockZone | null>(null);
  const resizeHandleRef = useRef<ResizeHandle>("se");
  const resizeNeighborsRef = useRef<Rect[]>([]);

  // Snapshot every OTHER open window's card rect straight off the DOM. Only rendered (open)
  // combat windows carry a `[data-gms-window]` card, so this returns exactly the active-mode
  // combat windows.
  const snapshotNeighbors = useCallback(
    (excludeId: string): Rect[] => {
      const rects: Rect[] = [];
      if (typeof document !== "undefined") {
        const o = originOf();
        for (const el of document.querySelectorAll<HTMLElement>("[data-gms-window]")) {
          if (el.dataset.gmsWindow === excludeId) continue;
          const r = el.getBoundingClientRect();
          rects.push({ x: r.left - o.left, y: r.top - o.top, w: r.width, h: r.height });
        }
      }
      return rects;
    },
    [originOf],
  );

  const stopCursorTracking = useCallback(() => {
    if (cursorMoveRef.current && typeof window !== "undefined") {
      window.removeEventListener("mousemove", cursorMoveRef.current);
    }
    cursorMoveRef.current = null;
  }, []);

  const beginDrag = useCallback(
    (id: string) => {
      dragIdRef.current = id;
      neighborsRef.current = snapshotNeighbors(id);
      // Track the cursor for the dock-zone preview (edge-hit → translucent overlay; committed on end).
      stopCursorTracking();
      if (typeof window !== "undefined") {
        const onMove = (e: MouseEvent) => {
          const o = originOf();
          const z = resolveDockZone({ x: e.clientX - o.left, y: e.clientY - o.top }, measureVp());
          if (z !== zoneRef.current) {
            zoneRef.current = z;
            setZone(z);
          }
        };
        cursorMoveRef.current = onMove;
        window.addEventListener("mousemove", onMove);
      }
    },
    [stopCursorTracking, snapshotNeighbors, originOf, measureVp],
  );

  const endDrag = useCallback(
    (id: string) => {
      // Commit the dock zone, if the cursor ended over one — it overrides the dragged position.
      const z = zoneRef.current;
      if (z) {
        const live = measureVp();
        const geo = zoneGeometry(z, live);
        apply((r) => setWindowGeometry({ reg: r, id, geo, vp: live }));
      }
      dragIdRef.current = null;
      neighborsRef.current = [];
      zoneRef.current = null;
      setZone(null);
      stopCursorTracking();
    },
    [apply, stopCursorTracking, measureVp],
  );

  const beginResize = useCallback(
    (id: string, handle: ResizeHandle) => {
      resizeHandleRef.current = handle;
      resizeNeighborsRef.current = snapshotNeighbors(id);
    },
    [snapshotNeighbors],
  );

  const endResize = useCallback(() => {
    resizeNeighborsRef.current = [];
  }, []);

  // Belt-and-braces: drop any live cursor listener if the surface unmounts mid-drag (a mode-flip
  // parks — and unmounts — the current mode's windows, but not this hook itself, so this is a
  // genuine belt-and-braces rather than the primary teardown).
  useEffect(() => stopCursorTracking, [stopCursorTracking]);

  // The snap-aware lifecycle bundle a host spreads onto a `WindowFrame`. Derives the live px
  // rect from the same stored % geometry `geometryOf` reads, so a caller that also reads
  // `geometryOf(id)` for `pos` never disagrees with what this bundle reports as `size`.
  const windowProps = useCallback(
    (id: string): CombatWindowProps => {
      const g = reg.geometry[id];
      const rect = pctToPx(g ?? expandSpec({ kind: "px", x: 80, y: 64, w: 600, h: 480 }, vp), vp, DEFAULT_MIN);
      return {
        onPos: (p: WindowPos) => {
          // During THIS window's own drag, snap the moving card to viewport + neighbour edges
          // before storing — a programmatic move (e.g. a reflow) never snaps.
          if (dragIdRef.current === id) {
            const card: Rect = { x: p.x + PAD_LEFT, y: p.y + PAD_TOP, w: rect.size.w, h: rect.size.h };
            const snapped = resolveSnap(card, neighborsRef.current, vp);
            apply((r) => moveWindow({ reg: r, id, pos: { x: snapped.x - PAD_LEFT, y: snapped.y - PAD_TOP }, vp }));
          } else {
            apply((r) => moveWindow({ reg: r, id, pos: p, vp }));
          }
        },
        size: rect.size,
        onSize: (s: WindowSize) => apply((r) => resizeWindow({ reg: r, id, size: s, vp })),
        windowId: id,
        onDragStart: () => beginDrag(id),
        onDragEnd: () => endDrag(id),
        onResizeStart: (handle: ResizeHandle) => beginResize(id, handle),
        onResize: (geo: { pos: WindowPos; size: WindowSize }) => {
          // `WindowFrame` already applied the per-handle origin-move + min clamp (pure resizeGeometry);
          // here we snap the gripped edge to viewport / neighbour lines (same engine as the drag)
          // and store pos+size together (`setWindowGeometry`) — a west/north handle moves the
          // origin too, so size-only `resizeWindow` can't carry it.
          const card: Rect = { x: geo.pos.x + PAD_LEFT, y: geo.pos.y + PAD_TOP, w: geo.size.w, h: geo.size.h };
          const snapped = resolveResizeSnap({
            handle: resizeHandleRef.current,
            rect: card,
            neighbors: resizeNeighborsRef.current,
            vp,
            min: DEFAULT_MIN,
          });
          apply((r) =>
            setWindowGeometry({
              reg: r,
              id,
              geo: { pos: { x: snapped.x - PAD_LEFT, y: snapped.y - PAD_TOP }, size: { w: snapped.w, h: snapped.h } },
              vp,
            }),
          );
        },
        onResizeEnd: () => endResize(),
      };
    },
    [reg, vp, apply, beginDrag, endDrag, beginResize, endResize],
  );

  // The translucent dock-zone preview rectangle (screen/card coordinates), or null when the drag
  // cursor isn't over a zone. `CombatApp` renders it via `DockZonePreview`.
  const zonePreview = useMemo<Rect | null>(() => {
    if (!zone) return null;
    const geo = zoneGeometry(zone, vp);
    return { x: geo.pos.x + PAD_LEFT, y: geo.pos.y + PAD_TOP, w: geo.size.w, h: geo.size.h };
  }, [zone, vp]);

  return {
    registry: reg,
    isOpen: (id: string) => isOpen(reg, id),
    zOf: (id: string) => zOf(reg, id),
    geometryOf: (id: string): WindowGeometry | undefined => {
      const g = reg.geometry[id];
      return g ? pctToPx(g, vp, DEFAULT_MIN) : undefined;
    },
    open,
    close,
    focus,
    move,
    resize,
    toggle,
    windowProps,
    zonePreview,
  };
}
